import type {
  ExternalValueRef,
  NodePropertyRef,
  NodeRole,
  PrimitiveValue,
  PrimitiveValueName,
  Value,
} from "@skilitics/nautilus-core";
import type { DesignerNodeData } from "@skilitics/nautilus-core-designer-data";

import type { DesignerNode, NodeManifestAny } from "../types/index.ts";
import type { NodePropertyOptions, NodePropertyOptionsForAnyValue } from "../types/node-designs/bindings.ts";
import type { GetNodeValue, ResolveAvailableValues } from "../values/querying/index.ts";
import type { ResolveDesignTimeValue } from "../values/resolveDesignTimeValue.ts";
import { resolveValueToRuntimeDynamicValueCreate } from "../values/resolveValueConfigs.ts";
import { resolveValueSignatureCreate } from "../values/resolveValueSignature.ts";
import type { GetValueSubtypeDefinition } from "../values/subtype-definitions/index.ts";

export interface BuildRuntimeBindingsOptions {
  /** Ancestor nodes in ascending order. */
  ancestors: DesignerNodeData[];
  parentContext?: Record<string, unknown>;
  getNodeValue: GetNodeValue;
  resolveAvailableValues: ResolveAvailableValues;
  resolveDesignTimeValue: ResolveDesignTimeValue;
  getValueSubtypeDefinition: GetValueSubtypeDefinition;
}

type Designer = NodeManifestAny["designer"];

/**
 * Runs a node designer's `getRuntimeBindings`, materialising the node's
 * published `bindings` section of `__dv` value refs. Returns undefined when the
 * manifest has no bindings to emit. Used by publish rendering and by design-time
 * analysis of the node-value reference graph.
 */
export const buildRuntimeBindings = (
  designerNode: DesignerNode,
  designer: Designer,
  {
    ancestors,
    parentContext,
    getNodeValue,
    resolveAvailableValues,
    resolveDesignTimeValue,
    getValueSubtypeDefinition,
  }: BuildRuntimeBindingsOptions,
) => {
  if (!designer.getRuntimeBindings) return undefined;

  const { id } = designerNode;

  const resolveAncestorId = (spec: { role: NodeRole } | { typeId: string }) => {
    if (!spec) {
      return id;
    }
    const matcher: (n: DesignerNodeData) => boolean =
      "role" in spec ? (n) => n.role === spec.role : (n) => n.typeId === spec.typeId;
    const ancestor = ancestors.find(matcher);
    if (!ancestor) {
      throw new Error(
        `Unable to find ancestor to spec ${JSON.stringify(spec)} generating runtime bindings for ${designerNode.typeId}:${id}`,
      );
    }
    return ancestor.id;
  };

  const resolveNodePropertyNodeId = (opts: NodePropertyOptions) => {
    if ("nodeId" in opts) {
      return opts.nodeId ?? id;
    } else if ("ancestor" in opts) {
      return resolveAncestorId(opts.ancestor);
    } else {
      return id;
    }
  };

  const nodeProperty = (
    type: PrimitiveValueName,
    options: NodePropertyOptionsForAnyValue,
    fallback: PrimitiveValue,
  ) => {
    const { property, fuzzy, invert } = options;
    return {
      __dv: {
        type: "np",
        meta: { type, fallback, ...(type === "boolean" ? { fuzzy, invert } : {}) },
        property,
        nodeId: resolveNodePropertyNodeId(options),
      } as NodePropertyRef,
    } as Value;
  };

  const configuredValue = resolveValueToRuntimeDynamicValueCreate({
    getNodeValue,
    resolveDesignTimeValue,
    getValueSubtypeDefinition,
  });

  return designer.getRuntimeBindings(designerNode, {
    parentContext,
    configuredValue,
    booleanNodeProperty: (...args) => nodeProperty("boolean", ...args),
    stringNodeProperty: (...args) => nodeProperty("string", ...args),
    numberNodeProperty: (...args) => nodeProperty("number", ...args),
    nodeProperty: (type: PrimitiveValueName, ...args) => nodeProperty(type, ...args),
    externalValue: (property, fallback, type = "string") =>
      ({ __dv: { type: "ext", meta: { type, fallback }, property } as ExternalValueRef }) as Value,
    resolveAvailableValues,
    resolveDesignTimeValue,
    resolveValueSignature: resolveValueSignatureCreate({ getNodeValue }),
  });
};
